import React from "react";
import { Image } from "primereact/image";
import { Link, useLocation } from "react-router-dom";
import { Dock } from "primereact/dock";
import { MenuItem } from "primereact/menuitem";
import { MenuItems } from "../../data/MenuItems";

interface SidebarProps {
  menuItems: MenuItems[];
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

type MenuNode = MenuItems & { children?: MenuItems[] };

const SidebarComponent: React.FC<SidebarProps> = ({
  isExpanded,
  setIsExpanded,
  menuItems,
}) => {
  const location = useLocation();

  const isActive = React.useCallback(
    (path?: string) => !!path && location.pathname === path,
    [location.pathname]
  );

  const hierarchicalMenu: MenuNode[] = React.useMemo(() => {
    const dashboardItem = menuItems.find((i) => i.path === "/dashboard");

    const payinServices = menuItems.find((i) => i.path === "/dashboard/payin/services");
    const payinHistory = menuItems.find((i) => i.path === "/dashboard/payin/history");
    const payoutServices = menuItems.find((i) => i.path === "/dashboard/payout/services");
    const payoutHistory = menuItems.find((i) => i.path === "/dashboard/payout/history");
    const payoutFundingsHistory = menuItems.find((i) => i.path === "/dashboard/payout/fundings-history");

    const excluded = new Set([
      "/dashboard",
      "/dashboard/payin/services",
      "/dashboard/payin/history",
      "/dashboard/payout/services",
      "/dashboard/payout/history",
      "/dashboard/payout/fundings-history",
    ]);

    const base = menuItems.filter((i) => !excluded.has(i.path));

    const payinGroup: MenuNode | null =
      payinServices || payinHistory
        ? {
            label: "Payin",
            icon: payinServices?.icon || "pi pi-wallet",
            path: "/dashboard/payin",
            children: [payinServices, payinHistory].filter(Boolean) as MenuItems[],
          }
        : null;

    const payoutGroup: MenuNode | null =
      payoutServices || payoutHistory || payoutFundingsHistory
        ? {
            label: "Payout",
            icon: payoutServices?.icon || "pi pi-money-bill",
            path: "/dashboard/payout",
            children: [payoutServices, payoutHistory, payoutFundingsHistory].filter(Boolean) as MenuItems[],
          }
        : null;

    return [
      ...(dashboardItem ? [dashboardItem] : []),
      ...(payinGroup ? [payinGroup] : []),
      ...(payoutGroup ? [payoutGroup] : []),
      ...base,
    ];
  }, [menuItems]);

  const [openMenus, setOpenMenus] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    const nextState: Record<string, boolean> = {};
    hierarchicalMenu.forEach((item) => {
      if (!item.children?.length) return;
      const key = item.path || item.label;
      nextState[key] = item.children.some((c) => isActive(c.path));
    });
    setOpenMenus((prev) => ({ ...nextState, ...prev }));
  }, [hierarchicalMenu, isActive]);

  const toggleMenu = (key: string) => {
    setOpenMenus((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const dockItems: MenuItem[] = menuItems.map((item) => ({
    label: item.label,
    icon: () => <i className={`${item.icon} text-xl`}></i>,
    command: () => setIsExpanded(false),
    template: (
      <Link
        to={item.path}
        className={`p-2 hover:bg-primary-100 rounded-full transition-all ${
          location.pathname === item.path ? "bg-primary-100 text-primary-500" : "text-primary-400"
        }`}
      >
        <i className={`${item.icon} text-xl`}></i>
      </Link>
    ),
  }));

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={`fixed z-50 top-0 left-0 h-screen shadow-sm hidden md:block bg-white transition-all duration-300 border-e ${
          isExpanded ? "w-64" : "w-24"
        }`}
      >
        <div className="">
          <div
            className={`transition-all duration-300 flex items-center p-4 ${
              isExpanded ? "justify-start" : "justify-center"
            }`}
          >
            <Image
              src={
                isExpanded
                  ? "/assets/images/logo.png"
                  : "/assets/images/logo-icon.png"
              }
              alt="wacepay logo"
              width={isExpanded ? "180" : "50"}
              className="transition-all duration-300"
            />
          </div>
          <div
            className=" overflow-y-auto"
            style={{ maxHeight: "calc(100vh - 100px)" }}
          >
            <div className="flex flex-col gap-1 px-3 py-2">
              {hierarchicalMenu.map((item, index) => {
                const prevSection = index > 0 ? hierarchicalMenu[index - 1].section : undefined;
                const showSectionHeader = isExpanded && item.section && item.section !== prevSection;

                const sectionHeader = showSectionHeader ? (
                  <div className={`${index !== 0 ? "mt-4 pt-3 border-t border-gray-100" : "mb-1"} px-2 mb-1`}>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 select-none">
                      {item.section}
                    </p>
                  </div>
                ) : null;

                if (item.children?.length) {
                  const key = item.path || item.label;
                  const isOpen = !!openMenus[key];
                  const parentActive = item.children.some((c) => isActive(c.path));

                  return (
                    <React.Fragment key={`${item.label}-${index}`}>
                      {sectionHeader}
                      <div className="rounded-lg">
                        <button
                          type="button"
                          onClick={() => toggleMenu(key)}
                          className={`w-full flex items-center ${isExpanded ? "" : "justify-center"} rounded-lg p-2 text-sm transition-colors ${
                            parentActive ? "bg-primary-50 text-primary-600" : "text-secondary-500 hover:bg-gray-50"
                          }`}
                        >
                          <i className={`${item.icon} ${isExpanded ? "mr-3" : ""} text-xl`}></i>
                          {isExpanded && (
                            <>
                              <span className="font-medium flex-1 text-left">{item.label}</span>
                              <i
                                className={`pi pi-chevron-down text-xs transition-transform duration-200 ${
                                  isOpen ? "rotate-180" : ""
                                }`}
                              />
                            </>
                          )}
                        </button>

                        {isExpanded && isOpen && (
                          <div className="ml-8 mt-1 flex flex-col gap-1">
                            {item.children.map((sub, subIdx) => {
                              const subActive = isActive(sub.path);
                              return (
                                <Link
                                  key={`${sub.label}-${subIdx}`}
                                  to={sub.path}
                                  className={`rounded-md px-2 py-1 text-sm transition-colors ${
                                    subActive
                                      ? "bg-primary-100 text-primary-600 font-medium"
                                      : "text-secondary-500 hover:bg-gray-50 hover:text-primary-500"
                                  }`}
                                >
                                  {sub.label}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                }

                const itemActive = isActive(item.path);
                return (
                  <React.Fragment key={`${item.label}-${index}`}>
                    {sectionHeader}
                    <Link
                      to={item.path}
                      className={`flex items-center ${isExpanded ? "" : "justify-center"} rounded-lg p-2 text-sm cursor-pointer transition-colors ${
                        itemActive
                          ? "bg-primary-100 text-primary-600"
                          : "text-secondary-500 hover:bg-gray-50 hover:text-primary-500"
                      }`}
                  >
                      <i className={`${item.icon} ${isExpanded ? "mr-3" : ""} text-xl`}></i>
                      {isExpanded && <span>{item.label}</span>}
                    </Link>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Dock for Small Screens */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden z-20 bg-white shadow-lg p-2">
        <Dock model={dockItems} position="bottom" className="p-2 rounded-2xl my-4 bg-black bg-opacity-10" />
      </div>
    </>
  );
};

export default SidebarComponent;
